import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SnackbarService } from '../services/snack.service';

// Spanish label for the entity names the backend reports in a 409 dependency-conflict
// response (see ShipmentExceptions.cs -> EntityHasActiveDependenciesException). Falls
// back to the raw backend name for anything not listed here.
const DEPENDENT_ENTITY_LABELS: Record<string, string> = {
  Origin: 'Origen',
  Destination: 'Destino',
  Route: 'Ruta',
};

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  constructor(private snackbarService: SnackbarService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((exception: HttpErrorResponse) => {
        // The login flow shows its own friendly message, so skip the generic toast.
        if (req.url.endsWith('/auth/login')) {
          return throwError(() => exception);
        }

        // The live wall shows these errors inside the camera's cell.
        if (req.url.includes('/MonitorMedia/live')) {
          return throwError(() => exception);
        }

        // The wall polls camera status and keeps the last one it got, so a failed poll isn't worth a toast.
        if (req.url.includes('/MonitorMedia/units/status')) {
          return throwError(() => exception);
        }

        let errorMessage: string;
        if (req.method === 'POST' && /\/commands\/[^/]+\//i.test(req.url)) {
          errorMessage =
            exception.status === 0
              ? 'No se pudo enviar el comando: revisa tu conexión a internet.'
              : 'No se pudo enviar el comando a la unidad. Intenta de nuevo en unos momentos.';
        } else if (exception.error instanceof ErrorEvent) {
          // Client-side error
          errorMessage = `Error: ${exception.error.message}`;
        } else if (exception.status === 409 && exception.error?.data?.dependentEntityName) {
          // Backend refused a delete because active dependent records still reference this entity
          // (e.g. a Client with active Origins, or an Origin with active Routes).
          const dependentCount = exception.error.data.dependentIds?.length ?? 0;
          const rawName = exception.error.data.dependentEntityName;
          const dependentName = DEPENDENT_ENTITY_LABELS[rawName] ?? rawName;
          errorMessage = `No se puede eliminar: tiene ${dependentCount} registro(s) de ${dependentName} relacionados.`;
        } else {
          // Server-side error
          errorMessage = `Error: ${exception.status} \n: ${exception.error?.title}`;
        }
        this.snackbarService.showError(errorMessage);
        return throwError(() => exception);
      }),
    );
  }
}
